import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { ArrowRight, Phone, Shield, Clock, Award, Hammer, ChevronRight } from "lucide-react";
import * as Icons from "lucide-react";
import SEO from "../components/SEO";
import { SITE, SERVICES, FLEET, STATS, SERVICE_AREAS } from "../lib/site";

const Icon = ({ name, className }) => {
  const I = Icons[name] || Icons.Wrench;
  return <I className={className} />;
};

export default function Home() {
  return (
    <>
      <SEO
        title="Mobile Crane Rental — DeLand, Central Florida"
        description="Alliance Crane Service LLC — 40-ton Grove & 50-ton Manitex mobile cranes with certified operators. Serving DeLand, Orlando, Daytona, Jacksonville, Titusville. 24/7 emergency lifting."
      />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/9906887/pexels-photo-9906887.jpeg"
            alt="Mobile crane operating at an industrial site in Central Florida"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent" />
          <div className="noise" />
        </div>

        <div className="relative max-w-[1400px] mx-auto px-5 md:px-8 pt-14 pb-20 md:pt-32 md:pb-40">
          <div className="kbd-badge mb-6" data-testid="hero-badge">
            <span className="w-1.5 h-1.5 bg-[var(--ac-red)] inline-block" />
            DeLand · Florida · Est. Heavy Lifting
          </div>
          <h1 className="font-display text-[2.75rem] leading-[0.95] sm:text-6xl lg:text-8xl max-w-4xl">
            HEAVY LIFTING.<br />
            <span className="text-[var(--ac-red)]">DONE RIGHT.</span>
          </h1>
          <p className="mt-6 text-base md:text-xl text-zinc-300 max-w-2xl leading-relaxed">
            Two hydraulic mobile cranes — a 40-ton Grove and 50-ton Manitex — paired with
            NCCCO-certified operators. Serving contractors and facility managers across
            Central Florida.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <Link to="/contact" data-testid="hero-cta-quote" className="btn-primary justify-center">
              Request a Quote <ArrowRight className="w-4 h-4" />
            </Link>
            <a href={`tel:${SITE.phoneE164}`} data-testid="hero-cta-call" className="btn-ghost justify-center">
              <Phone className="w-4 h-4" /> {SITE.phone}
            </a>
          </div>

          {/* Stats Row */}
          <div className="mt-14 md:mt-16 grid grid-cols-2 md:grid-cols-4 border-t border-white/10">
            {STATS.map((s, i) => (
              <div
                key={s.label}
                className={`py-5 md:py-6 ${i < 3 ? "md:border-r border-white/10" : ""} ${i < 2 ? "border-r border-white/10 md:border-r" : ""}`}
              >
                <div className="font-display text-3xl md:text-5xl text-white">{s.value}</div>
                <div className="overline mt-2">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="bg-black border-y border-white/10 py-4 md:py-6 overflow-hidden">
        <Marquee gradient={false} speed={45}>
          {SERVICE_AREAS.map((a) => (
            <div key={a.city} className="marquee-text px-5 md:px-8 flex items-center">
              <span>{a.city.toUpperCase()}</span>
              <span className="text-[var(--ac-red)] text-2xl md:text-3xl mx-5 md:mx-8 -mt-2">/</span>
            </div>
          ))}
        </Marquee>
      </section>

      {/* SERVICES */}
      <section className="py-16 md:py-28">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 mb-14">
            <div className="md:col-span-5">
              <div className="overline mb-4">01 / Services</div>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[0.95]">
                Built for the<br />jobs others<br />won't touch.
              </h2>
            </div>
            <div className="md:col-span-6 md:col-start-7 flex items-end">
              <p className="text-zinc-400 text-base md:text-lg leading-relaxed">
                From a single HVAC rooftop set to multi-day steel erection, we plan the lift,
                rig it safely and put steel where it belongs — on time, on budget.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-l border-[var(--ac-border)]">
            {SERVICES.map((s, i) => (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                data-testid={`service-card-${s.slug}`}
                className="hover-card-border border-b border-r border-[var(--ac-border)] p-7 md:p-9 group block bg-[var(--ac-bg)]"
              >
                <div className="flex items-start justify-between mb-8">
                  <div className="w-12 h-12 border border-[var(--ac-border)] flex items-center justify-center bg-black">
                    <Icon name={s.icon} className="w-5 h-5 text-[var(--ac-red)]" />
                  </div>
                  <span className="font-mono-data text-xs text-zinc-500">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <h3 className="font-display text-2xl md:text-3xl leading-tight mb-3">
                  {s.title}
                </h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{s.short}</p>
                <div className="mt-7 flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-bold text-[var(--ac-red)] opacity-0 group-hover:opacity-100 transition-opacity">
                  Learn more <ChevronRight className="w-3 h-3" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FLEET PREVIEW */}
      <section className="py-16 md:py-28 bg-[var(--ac-bg-2)] border-y border-[var(--ac-border)]">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-6">
            <div>
              <div className="overline mb-4">02 / Fleet</div>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl">Two cranes. One standard.</h2>
            </div>
            <Link to="/fleet" data-testid="home-fleet-link" className="btn-ghost">
              View Full Fleet <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {FLEET.map((f) => (
              <Link
                key={f.slug}
                to={`/fleet#${f.slug}`}
                data-testid={`fleet-card-${f.slug}`}
                className="group block surface hover-card-border overflow-hidden"
              >
                <div className="aspect-[16/10] overflow-hidden bg-black">
                  <img
                    src={f.image}
                    alt={f.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-7">
                  <div className="flex items-center justify-between mb-2">
                    <div className="overline">{f.type}</div>
                    <div className="font-mono-data text-[var(--ac-red)]">{f.capacity}</div>
                  </div>
                  <h3 className="font-display text-3xl md:text-4xl mb-2">{f.name}</h3>
                  <p className="text-sm text-zinc-400">{f.blurb}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="py-16 md:py-28">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-5">
              <div className="overline mb-4">03 / Why Alliance</div>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[0.95]">
                Safety isn't a slogan. It's our service.
              </h2>
              <p className="mt-6 text-zinc-400 leading-relaxed max-w-md">
                Every lift starts with a plan, every operator is certified, and every job leaves
                the site cleaner than we found it.
              </p>
            </div>
            <div className="md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-px bg-[var(--ac-border)]">
              {[
                { icon: Shield, title: "Licensed & Insured", body: "Full general liability and umbrella coverage. Certificates on request." },
                { icon: Award, title: "NCCCO Certified", body: "Operators carry current NCCCO certification and OSHA training." },
                { icon: Clock, title: "On-Time Mobilization", body: "Dispatchable rapid response from DeLand to every Central FL market." },
                { icon: Hammer, title: "Job-Site Ready", body: "Pre-lift plans, signal training, and ground-bearing assessments included." },
              ].map((b) => (
                <div key={b.title} className="bg-[var(--ac-bg)] p-7">
                  <b.icon className="w-6 h-6 text-[var(--ac-red)] mb-4" />
                  <div className="font-display text-xl mb-2">{b.title}</div>
                  <p className="text-sm text-zinc-400 leading-relaxed">{b.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* GOOGLE REVIEWS CTA */}
      <section className="bg-[var(--ac-bg-2)] border-y border-[var(--ac-border)]">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8 py-12 md:py-14 grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 items-center">
          <div className="md:col-span-7">
            <div className="overline mb-3">Reviews</div>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl leading-tight">
              Worked with us? <span className="text-[var(--ac-red)]">Leave a Google review.</span>
            </h2>
            <p className="mt-3 text-zinc-400 max-w-xl leading-relaxed">
              Your feedback helps other Central Florida contractors find us — and helps us
              keep raising the bar.
            </p>
          </div>
          <div className="md:col-span-5 flex flex-col gap-3">
            <div className="flex items-center gap-1 text-[var(--ac-yellow)]" aria-hidden="true">
              {[0,1,2,3,4].map((i) => (
                <svg key={i} className="w-6 h-6 md:w-7 md:h-7" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2l2.9 6.9 7.1.6-5.4 4.7 1.6 7-6.2-3.8-6.2 3.8 1.6-7L2 9.5l7.1-.6L12 2z"/>
                </svg>
              ))}
            </div>
            <a
              href={SITE.googleReviewUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="home-google-review-cta"
              className="btn-primary justify-center"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M12 2l2.9 6.9 7.1.6-5.4 4.7 1.6 7-6.2-3.8-6.2 3.8 1.6-7L2 9.5l7.1-.6L12 2z"/>
              </svg>
              Leave a Google Review
            </a>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden border-t border-[var(--ac-border)]">
        <div className="diag-stripes h-3 w-full" />
        <div className="max-w-[1400px] mx-auto px-5 md:px-8 py-16 md:py-24 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10 items-center">
          <div className="md:col-span-7">
            <div className="overline mb-4 text-[var(--ac-red)]">Ready to Lift?</div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[0.95]">
              Got a pick coming up?<br /> Let&apos;s plan it together.
            </h2>
          </div>
          <div className="md:col-span-5 flex flex-col gap-3">
            <Link to="/contact" data-testid="bottom-cta-quote" className="btn-primary justify-center">
              Request a Quote <ArrowRight className="w-4 h-4" />
            </Link>
            <a href={`tel:${SITE.phoneE164}`} data-testid="bottom-cta-call" className="btn-ghost justify-center">
              <Phone className="w-4 h-4" /> {SITE.phone}
            </a>
            <div className="text-xs text-zinc-500 mt-2 font-mono-data uppercase tracking-wider">
              {SITE.hours} · {SITE.emergencyHours}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
